import type { Theme } from "@mui/material";
import { lighten, useTheme } from "@mui/material";
import { darken } from "@mui/system";
import type { ColorPicker } from "../colorPicker/ColorPicker.types";
import { createSxStyles } from "../createSxStyles";
import type { ChipProps } from "./Chip";

export const useChipStyles = (color: ColorPicker, props: ChipProps) => {
    const theme = useTheme();

    const isSelected = props.isSelected;
    const isAutoComplete = props.isAutocomplete;
    const clickable = props.onClick !== undefined;
    const fontSize = getFontSize(props, theme);

    const backgroundColor =
        props.alternativeColor !== undefined
            ? props.alternativeColor.backgroundColor
            : isSelected || isAutoComplete
            ? theme.palette.primary.light
            : theme.palette.grey[300];

    const borderWith = "1px solid";
    return createSxStyles({
        chip: {
            fontSize: fontSize,
            outline: isSelected || isAutoComplete ? borderWith + theme.palette.primary.main : "",
            backgroundColor: backgroundColor,
            borderColor: isAutoComplete ? theme.palette.primary.main : "inherit",
            color: props.alternativeColor?.color ?? theme.palette.primary.main,
            "& .MuiChip-label": {
                color: isAutoComplete ? theme.palette.primary.dark : undefined,
            },
            "&:focus": {
                backgroundColor: backgroundColor,
            },
            "&:hover": {
                cursor: clickable ? "pointer" : "default",
                backgroundColor: clickable ? undefined : darken(backgroundColor, 0.05),
            },
        },
        chipColored: {
            fontSize: fontSize,
            outline: isSelected || isAutoComplete ? borderWith + color.strokeColor : "",
            backgroundColor: color.backgroundColor,
            color: theme.palette.primary.main,
            borderColor: color.strokeColor,
            marginRight: isAutoComplete ? theme.spacing(0.5) : undefined,
            "& .MuiChip-label": {
                color: color.strokeColor,
            },
            "&:focus": {
                backgroundColor: color.backgroundColor,
            },
            "&:hover": {
                cursor: clickable ? "pointer" : "default",
                backgroundColor: clickable ? darken(color.backgroundColor, 0.05) : color.backgroundColor,
            },
            "& .MuiSvgIcon-root": {
                fill: color.strokeColor,
            },
            "& .MuiChip-deleteIcon": {
                fill: color.strokeColor,
                "&:hover": {
                    fill: lighten(color.strokeColor, 0.3),
                },
            },
        },
    });
};

function getFontSize(props: ChipProps, theme: Theme) {
    switch (props.textSize) {
        case "xs":
            return theme.typography.pxToRem(9);
        case "s":
            return theme.typography.pxToRem(11);
        case "l":
            return theme.typography.pxToRem(15);
        case "xl":
            return theme.typography.pxToRem(17);
        case "xxl":
            return theme.typography.pxToRem(19);
        default:
            return theme.typography.pxToRem(12);
    }
}
